import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, TrendingUp, Users, Sparkles, Send, Mail, Reply as ReplyIcon } from "lucide-react";
import {
    BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import { toast } from "sonner";
import { extrasApi, bl } from "@/lib/extrasApi";
import { useI18n } from "@/lib/i18n";
import { StatusBadge } from "@/components/find/atoms";

const STAGE_COLORS = ["#71717A", "#3B82F6", "#6366F1", "#06B6D4", "#10B981", "#F43F5E"];

export default function Analyze() {
    const { locale } = useI18n();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        extrasApi.analyzeOverview()
            .then(setData)
            .catch(() => toast.error("Failed to load"))
            .finally(() => setLoading(false));
    }, []);

    if (loading || !data) {
        return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-zinc-500" /></div>;
    }

    const funnelSum = data.funnel.reduce((s, x) => s + x.count, 0);

    return (
        <div className="px-6 lg:px-10 py-6">
            <div className="mb-6">
                <h1 className="font-display font-black text-3xl tracking-tighter">{bl("Analytics", "Analyse", locale)}</h1>
                <p className="mt-1 text-sm text-zinc-500">{bl("A revenue-grade read-out of your BOXEB2B workspace.", "Un rapport grade revenue de ton workspace BOXEB2B.", locale)}</p>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Kpi label={bl("Leads", "Leads", locale)} value={data.leads_total} icon={Users} testKey="leads" />
                <Kpi label={bl("Revealed", "Révélés", locale)} value={data.leads_revealed} sub={`${Math.round(data.reveal_rate * 100)}%`} icon={Sparkles} testKey="revealed" />
                <Kpi label={bl("Sent", "Envoyés", locale)} value={data.campaigns_stats.sent} icon={Send} testKey="sent" />
                <Kpi label={bl("Open rate", "Ouverture", locale)} value={`${(data.open_rate * 100).toFixed(1)}%`} icon={Mail} testKey="openrate" />
                <Kpi label={bl("Reply rate", "Réponse", locale)} value={`${(data.reply_rate * 100).toFixed(1)}%`} icon={ReplyIcon} testKey="replyrate" />
            </div>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Funnel */}
                <div className="lg:col-span-2 rounded-2xl p-6 bg-brand-surface border border-white/8">
                    <div className="text-sm font-semibold mb-4">{bl("Pipeline funnel", "Funnel pipeline", locale)}</div>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                            <BarChart data={data.funnel} layout="vertical" margin={{ left: 10, right: 20 }}>
                                <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false} />
                                <XAxis type="number" tick={{ fill: "#71717A", fontSize: 11 }} axisLine={false} tickLine={false} />
                                <YAxis type="category" dataKey="stage" tick={{ fill: "#A1A1AA", fontSize: 12 }} axisLine={false} tickLine={false} width={80} />
                                <Tooltip contentStyle={{ background: "#1A1A2E", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
                                <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                                    {data.funnel.map((_, i) => <Cell key={i} fill={STAGE_COLORS[i]} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-2 text-xs text-zinc-500 font-mono">{funnelSum} {bl("leads across stages", "leads répartis", locale)}</div>
                </div>

                {/* Top industries */}
                <div className="rounded-2xl p-6 bg-brand-surface border border-white/8">
                    <div className="text-sm font-semibold mb-4">{bl("Top industries", "Top secteurs", locale)}</div>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                            <PieChart>
                                <Pie
                                    data={data.top_industries}
                                    dataKey="count"
                                    nameKey="name"
                                    cx="50%" cy="50%"
                                    innerRadius={40}
                                    outerRadius={80}
                                    paddingAngle={2}
                                >
                                    {data.top_industries.map((_, i) => <Cell key={i} fill={["#6366F1", "#8B5CF6", "#06B6D4", "#10B981", "#F59E0B", "#F43F5E", "#EC4899", "#22D3EE"][i % 8]} />)}
                                </Pie>
                                <Tooltip contentStyle={{ background: "#1A1A2E", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
                                <Legend wrapperStyle={{ fontSize: 10, color: "#A1A1AA" }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Weekly sends */}
                <div className="lg:col-span-2 rounded-2xl p-6 bg-brand-surface border border-white/8">
                    <div className="text-sm font-semibold mb-4">{bl("This week — emails sent", "Cette semaine — emails envoyés", locale)}</div>
                    <div className="h-52">
                        <ResponsiveContainer width="100%" height="100%" minHeight={180}>
                            <BarChart data={data.weekly_sends}>
                                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis dataKey="day" tick={{ fill: "#71717A", fontSize: 11 }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: "#71717A", fontSize: 11 }} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={{ background: "#1A1A2E", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
                                <Bar dataKey="value" fill="#6366F1" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Popular ICPs */}
                <div className="rounded-2xl p-6 bg-brand-surface border border-white/8">
                    <div className="text-sm font-semibold mb-4">{bl("Popular ICPs", "ICP populaires", locale)}</div>
                    {data.popular_icps.length === 0 ? (
                        <div className="text-xs text-zinc-500">{bl("Run AI prospecting to grow this list.", "Lance des prospections IA pour peupler.", locale)}</div>
                    ) : (
                        <ul className="space-y-2">
                            {data.popular_icps.map((p, i) => (
                                <li key={i} className="text-xs text-zinc-300">
                                    <div className="truncate">{p.query}</div>
                                    <div className="font-mono text-[10px] text-brand-cyan">{p.matches} matches</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Top campaigns */}
                <div className="lg:col-span-3 rounded-2xl p-6 bg-brand-surface border border-white/8">
                    <div className="text-sm font-semibold mb-4">{bl("Top campaigns", "Top campagnes", locale)}</div>
                    {data.top_campaigns.length === 0 ? (
                        <div className="text-xs text-zinc-500">{bl("Launch a campaign to see performance.", "Lance une campagne pour voir les perfs.", locale)}</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-xs uppercase text-zinc-500 border-b border-white/8"><tr>
                                    <th className="p-2 text-left">Campaign</th><th className="p-2 text-left">Sent</th><th className="p-2 text-left">Replies</th><th className="p-2 text-left">Reply %</th>
                                </tr></thead>
                                <tbody>
                                    {data.top_campaigns.map((c) => (
                                        <tr key={c.campaign_id} className="border-b border-white/5">
                                            <td className="p-2 text-white">{c.name}</td>
                                            <td className="p-2 font-mono text-zinc-300">{c.sent}</td>
                                            <td className="p-2 font-mono text-zinc-300">{c.replies}</td>
                                            <td className="p-2 font-mono text-brand-cyan">{(c.reply_rate * 100).toFixed(1)}%</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Kpi({ label, value, sub, icon: Icon, testKey }) {
    return (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl p-4 bg-brand-surface border border-white/8" data-testid={`analyze-kpi-${testKey}`}>
            <div className="flex items-start justify-between">
                <div className="text-xs text-zinc-500">{label}</div>
                <Icon size={14} className="text-brand-cyan" />
            </div>
            <div className="mt-2 font-display font-black text-2xl tracking-tighter">{value}</div>
            {sub && <div className="mt-0.5 text-[11px] font-mono text-brand-cyan">{sub}</div>}
        </motion.div>
    );
}
